import { getMemberCsrfToken, fetchMemberCsrf } from "@/services/memberAuth";
import { EcpayLogisticsSubType, LogisticsProvider, ShippingDeliveryType } from "@/services/shipping";

export type OrderItem = {
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string;
  Sku: string;
  ImageUrl: string;
  UnitPrice: number;
  Quantity: number;
  LineTotal: number;
};

export type OrderStatus = "Pending" | "Processing" | "Shipped" | "Completed" | "Cancelled";
export type PaymentStatus = "Unpaid" | "Paid" | "Refunded" | "PartiallyRefunded";
/**
 * 付款方式改由後台 PaymentMethod 資料表管理（比照配送方式做法），代碼不再限於固定的兩種字面值，
 * 這裡的 "COD"/"BankTransfer" 只是隨系統預設值保留的常見範例，實際上可以是後台自訂的任何代碼
 * （例如 Gateway 類型的 "CREDIT_CARD"）。畫面顯示一律優先使用 Order.PaymentMethodName（下單當下的名稱快照）。
 */
export type PaymentMethod = string;

/** 電子發票買受人類型：個人（可選載具）、公司戶（統一編號）、捐贈。 */
export type InvoiceBuyerType = "Personal" | "Company" | "Donation";
/** 電子發票載具類型，只有 InvoiceBuyerType=Personal 時有意義。 */
export type InvoiceCarrierType = "None" | "MobileBarcode" | "CitizenCertificate" | "Member";

export type Order = {
  OrderId: string;
  OrderNumber: string;
  MemberId: string | null;
  RecipientName: string;
  RecipientPhone: string;
  RecipientEmail: string;
  ShippingCity: string;
  ShippingDistrict: string;
  ShippingPostalCode: string | null;
  ShippingAddressLine: string;
  Notes: string | null;
  PaymentMethod: PaymentMethod;
  /** 下單當下的付款方式名稱快照；此欄位新增前建立的極少數歷史訂單可能為 null，顯示時請 fallback 到 PaymentMethod 代碼。 */
  PaymentMethodName: string | null;
  /** 下單當下的付款說明快照（後台在付款方式設定的「付款說明」，例如銀行轉帳帳號資訊）；沒有設定或此欄位新增前的歷史訂單為 null。 */
  PaymentInstructions: string | null;
  /** 已退款金額（來自 Refund 資料表的累計加總快取），0 表示尚未有任何退款紀錄。 */
  RefundedAmount: number;
  /** 付款方式手續費（例如信用卡刷卡手續費），已計入 TotalAmount。 */
  PaymentExtraFee: number;
  PaymentStatus: PaymentStatus;
  PaymentDueAt: string | null;
  OrderStatus: OrderStatus;
  Items: OrderItem[];
  SubtotalAmount: number;
  /** 優惠碼折抵的商品金額（FixedAmount／Percentage 類型）；FreeShipping 類型的優惠碼折抵的是運費，此欄位為 0。 */
  DiscountAmount: number;
  ShippingFee: number;
  ShippingMethodName: string | null;
  ShippingDeliveryType: ShippingDeliveryType;
  PickupLocationId: string | null;
  PickupLocationName: string | null;
  PickupLocationAddress: string | null;
  PickupLocationPhone: string | null;
  LogisticsProvider: LogisticsProvider | null;
  LogisticsSubType: EcpayLogisticsSubType | null;
  LogisticsStatus: string | null;
  LogisticsTradeNo: string | null;
  ProviderLogisticsId: string | null;
  CvsStoreId: string | null;
  CvsStoreName: string | null;
  CvsStoreAddress: string | null;
  CvsStoreTelephone: string | null;
  CvsPaymentNo: string | null;
  CvsValidationNo: string | null;
  IsLogisticsCollection: boolean;
  LogisticsCreatedAt: string | null;
  ShippingCouponCode: string | null;
  TotalAmount: number;
  AdminNote: string | null;
  /** 結帳當下選擇的電子發票資料快照，見 InvoiceIssuer 說明；實際開立結果（發票號碼等）請另外查詢訂單的發票紀錄。 */
  InvoiceBuyerType: InvoiceBuyerType;
  InvoiceBuyerUBN: string | null;
  InvoiceBuyerName: string | null;
  InvoiceCarrierType: InvoiceCarrierType;
  InvoiceCarrierNumber: string | null;
  InvoiceLoveCode: string | null;
  CreatedAt: string | null;
  UpdatedAt: string | null;
};

export type PaginationMeta = {
  Page: number;
  PerPage: number;
  Total: number;
  TotalPages: number;
};

export class OrderApiError extends Error {
  errors?: Record<string, string>;

  constructor(message: string, errors?: Record<string, string>) {
    super(message);
    this.errors = errors;
  }
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw new OrderApiError(data.message || "操作失敗，請稍後再試。", data.errors);
  }
  return data as T;
}

export type CreateOrderPayload = {
  Items: { ProductId: number; VariantId: number | null; Quantity: number }[];
  RecipientName: string;
  RecipientPhone: string;
  RecipientEmail: string;
  ShippingCity: string;
  ShippingDistrict: string;
  ShippingPostalCode?: string;
  ShippingAddressLine: string;
  Notes?: string;
  PaymentMethodId: string;
  ShippingMethodId: string;
  PickupLocationId?: string;
  PickupLocationName?: string;
  PickupLocationAddress?: string;
  PickupLocationPhone?: string;
  ShippingCouponCode?: string;
  InvoiceBuyerType: InvoiceBuyerType;
  InvoiceBuyerUBN?: string;
  InvoiceBuyerName?: string;
  InvoiceCarrierType?: InvoiceCarrierType;
  InvoiceCarrierNumber?: string;
  InvoiceLoveCode?: string;
};

export type PaymentRedirectPayload = {
  ActionUrl: string;
  Fields: Record<string, string>;
  Method: string;
} | null;

export type CreateOrderResult = {
  order: Order;
  paymentRedirect: PaymentRedirectPayload;
};

async function postOrder(payload: CreateOrderPayload, isCsrfRetry = false): Promise<CreateOrderResult> {
  const response = await fetch("/api/orders", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getMemberCsrfToken(),
    },
    body: JSON.stringify(payload),
  });

  // 跟 memberAuth.ts 的 request() 是同一種修法：結帳的 CSRF Token 一樣快取在 sessionStorage，
  // 一樣有可能因為 Session 過期等原因跟目前 Session 對不上，導致送出訂單時收到 403「結帳驗證逾時」。
  // 這裡改成：偵測到 403 就自動換一個新 Token 再重送一次原本的訂單內容，最多重試一次，
  // 避免使用者必須自己重新整理頁面、重新填一次收件資訊才能送出訂單（結帳失敗是最不該發生體驗斷點的地方）。
  if (response.status === 403 && !isCsrfRetry) {
    await fetchMemberCsrf();
    return postOrder(payload, true);
  }

  const data = await parseJsonResponse<{ ok: boolean; Order: Order; PaymentRedirect: PaymentRedirectPayload }>(response);
  return { order: data.Order, paymentRedirect: data.PaymentRedirect };
}

export async function createOrder(payload: CreateOrderPayload) {
  if (!getMemberCsrfToken()) {
    await fetchMemberCsrf();
  }
  return postOrder(payload);
}

export async function getOrderByNumber(orderNumber: string, contact?: string) {
  const query = contact ? `?Contact=${encodeURIComponent(contact)}` : "";
  const data = await fetch(`/api/orders/${encodeURIComponent(orderNumber)}${query}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Order: Order }>(response));
  return data.Order;
}

/** 非會員（訪客）以訂購 Email＋聯絡電話查詢訂單列表，不需記住訂單編號。 */
export async function lookupOrdersByContact(email: string, phone: string, page = 1, perPage = 10) {
  const query = new URLSearchParams({ Email: email, Phone: phone, Page: String(page), PerPage: String(perPage) });
  const data = await fetch(`/api/orders/lookup?${query.toString()}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Orders: Order[]; Pagination: PaginationMeta }>(response));
  return data;
}

export async function getMemberOrders(page = 1, perPage = 10) {
  const data = await fetch(`/api/member/orders?Page=${page}&PerPage=${perPage}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Orders: Order[]; Pagination: PaginationMeta }>(response));
  return data;
}

export const orderStatusLabels: Record<OrderStatus, string> = {
  Pending: "待處理",
  Processing: "處理中",
  Shipped: "已出貨",
  Completed: "已完成",
  Cancelled: "已取消",
};

export const paymentStatusLabels: Record<PaymentStatus, string> = {
  Unpaid: "待付款",
  Paid: "已付款",
  Refunded: "已退款",
  PartiallyRefunded: "部分退款",
};

export const paymentMethodLabels: Record<PaymentMethod, string> = {
  COD: "貨到付款",
  BankTransfer: "銀行轉帳",
};

export const invoiceBuyerTypeLabels: Record<InvoiceBuyerType, string> = {
  Personal: "個人發票",
  Company: "統一編號發票",
  Donation: "捐贈發票",
};

export const invoiceCarrierTypeLabels: Record<InvoiceCarrierType, string> = {
  None: "索取紙本",
  MobileBarcode: "手機條碼載具",
  CitizenCertificate: "自然人憑證載具",
  Member: "會員載具",
};
